import { matchPath } from 'react-router-dom';
import { NEWEST_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import {
  SUCCESS, PRISTINE, PENDING, FAILURE,
} from 'utils/constants/queryStatuses';
import { questionnaireApi } from 'modules/questionnaire/api';
import { TRIAL_TYPES } from 'utils/constants/trial';
import { ROUTE_PATH } from 'utils/constants/router';
import { store } from 'utils/store';
import { setChildPopup, setPopup } from 'modules/popup/actions';
import { authenticationActions } from 'components/Authentication/actions';
import { appConfigurationActions } from 'modules/appConfiguration/actions';
import { getQuestions } from 'new-ui/functions/getQuestions';
import { sendGoogleAnalyticsEvent } from 'modules/ga';
import { hideBackdrop, showBackdrop } from 'modules/backdrop/actions';
import { QUESTION_IDS_BY_DISEASE } from 'modules/questionnaire/constants';
import { matchesActions } from 'modules/matches/actions';
import { intercom } from 'modules/intercom/intercom';
import { setFieldValue } from 'components/Questionnaire/actions';
import { setAppStatus } from 'modules/appStatus/appStatus.actions';

class App {
  constructor() {
    this.initializationStatus = PRISTINE;
    this.store = store;
    this.cache = {};
    this.intercom = intercom;
  }

  get user() {
    const { profile: { data } } = this.store.getState();
    return data;
  }

  // eslint-disable-next-line class-methods-use-this
  set user(v) {
    throw new Error('forbidden');
  }

  get matches() {
    const { matches } = this.store.getState();
    return matches;
  }

  // eslint-disable-next-line class-methods-use-this
  set matches(v) {
    throw new Error('forbidden');
  }

  get trials() {
    const { matches: { trials } } = this.store.getState();
    return trials;
  }

  // eslint-disable-next-line class-methods-use-this
  set trials(v) {
    throw new Error('forbidden');
  }

  get treatments() {
    const { matches: { treatments } } = this.store.getState();
    return treatments;
  }

  // eslint-disable-next-line class-methods-use-this
  set treatments(v) {
    throw new Error('forbidden');
  }

  get treatmentsCategories() {
    const { matches: { treatmentsCategories } } = this.store.getState();
    return treatmentsCategories;
  }

  // eslint-disable-next-line class-methods-use-this
  set treatmentsCategories(v) {
    throw new Error('forbidden');
  }

  async init(setInitCounter) {
    if (this.initializationStatus === PENDING) {
      return;
    }
    this.initializationStatus = PENDING;

    try {
      const [drugs, biomarkers] = await Promise.all([
        questionnaireApi.getDrugs(),
        questionnaireApi.getBiomarkers(),
      ]);

      this.drugs = drugs;
      this.biomarkers = [{
        code: 'none',
        name: 'general.no_known_biomarkers',
      }, ...biomarkers];

      this.initializationStatus = SUCCESS;
    } catch (ex) {
      this.initializationStatus = FAILURE;
      this.store.dispatch(setAppStatus({ status: FAILURE }));
    } finally {
      setInitCounter((v) => v + 1);
    }
  }

  async initQuestions(condition, additionalParams) {
    // in the case of HCP stored in this.questions Questionnaire doesn't match to a selected patient
    // this.questions is often reassigned by Intake, Feed/Widgets fetched without additionalParams
    try {
      this.questions = await getQuestions(condition, additionalParams);
    } catch {
    }
  }

  isSOCDisabled() {
    const { appConfiguration: { providerSpecific: { disabledTrialTypes } = {} } } = this.store.getState();
    return (disabledTrialTypes ?? []).includes(TRIAL_TYPES.TREATMENT);
  }

  async requestRematch() {
    const params = new URLSearchParams(window.location.search);

    this.store.dispatch(matchesActions.requestMatches({
      requestToRematch: true,
      newest: params.get(NEWEST_URL_SEARCH_PARAM),
    }));
  }

  isCurrentPage(routeName) {
    return !!matchPath(window.location.pathname, {
      path: ROUTE_PATH[routeName],
      exact: true,
    });
  }

  numResults(onlyTrials) {
    if (onlyTrials) return (this.trials?.length || 0);
    return ((this.trials?.length || 0) + (this.treatments?.length || 0));
  }

  set coniguration(v) {
    throw new Error('forbidden');
  }

  get coniguration() {
    return this.store.getState().appConfiguration;
  }

  async updateUserConfiguration(dataToUpdate = {}) {
    if (this.coniguration.status !== SUCCESS) {
      return;
    }

    await this.store.dispatch(appConfigurationActions.updateUserConfiguration(dataToUpdate));
  }

  get popup() {
    return this.store.getState().popup.popup;
  }

  // eslint-disable-next-line class-methods-use-this
  set popup(_) {
    throw new Error('forbidden');
  }

  hasTrialsMissingCritical() {
    return this.trials.some((t) => t.missed_questions.length);
  }

  updateUser({ data, actionSource = null } = {}) {
    // data to update in not required as some code (e.g. Intake) mutates app.user
    const { user } = this;
    return this.store.dispatch(authenticationActions.updateUser(
      {
        user: data ?? user,
      },
      { source: actionSource },
    ));
  }

  async handleUserUpdate({ data, actionSource = null } = {}) {
    this.store.dispatch(matchesActions.setMatchesIsStale());
    await this.updateUser({ data, actionSource });
    await this.requestRematch();
  }

  showPreloader({ reason } = {}) {
    this.store.dispatch(showBackdrop({ reason }));
  }

  hidePreloader({ reason } = {}) {
    this.store.dispatch(hideBackdrop({ reason }));
  }

  async answerQuestion(questions) {
    if (questions.user_intent) {
      await this.updateUserConfiguration({
        userIntent: {
          other: '',
          type: questions.user_intent.toUpperCase(),
        },
      });
      return;
    }

    const { user } = this;

    const setValueToUserProfilersQuestion = (question) => {
      const [questionKey, value] = question;
      let questionId = questionKey;
      if (questionKey === 'current_stage') {
        questionId = QUESTION_IDS_BY_DISEASE[user.personal.condition]?.patientStage || 'patient_stage';
      }
      this.store.dispatch(
        setFieldValue({
          id: questionId,
          value,
        }),
      );
      // !!! user mutation
      user.condition_profile[questionId] = value;
    };

    for (const question in questions) {
      setValueToUserProfilersQuestion([question, questions[question]]);
    }

    await this.handleUserUpdate({ data: user });
  }

  sendGoogleAnalyticsEvent(category, eventName, metaData) {
    const { appConfiguration: { providerSpecific: { code: providerCode } = {} } } = this.store.getState();
    sendGoogleAnalyticsEvent(
      category,
      eventName,
      {
        ...(metaData ?? {}),
        provider: providerCode,
      },
    );
  }

  setPopup(type, data = {}) {
    const { isChild, ...restData } = data;
    if (isChild) {
      this.store.dispatch(setChildPopup(
        type
          ? {
            type,
            data: restData,
          }
          : false,
      ));
      return;
    }

    this.store.dispatch(setPopup(
      type ? {
        type,
        data: restData,
      } : false,
    ));
  }
}

const app = new App();
window.app = app;
export default app;
